import { Flex, Spinner, Divider, Box } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { AccountNameEditable } from "../../components/organisms/accounts/AccountNameEditable";
import { NicknameEditable } from "../../components/organisms/accounts/NicknameEditable";
import { useClipboardToast } from "../../hooks/useClipboardToast";
import { useCurrentAccountStore } from "../../store/currentAccountStore";
import { useQueryGetAccount } from "../../store/useQueryGetAccount";
import { Account } from "../../types/Account";
import { ReadOnlySettingRow } from "./components/ReadOnlySettingRow";
import { SettingRowBase } from "./components/SettingRowBase";
import {
  SectionHeader,
  SectionContainer,
  SettingsStack,
} from "./components/SettingsComponents";

export function AccountGeneralSettings() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const queryGetAccount = useQueryGetAccount(accountId);

  const onCopyBglId = useClipboardToast({
    value: queryGetAccount.data?._id,
    toastOptions: {
      title: "Copied BGL account ID",
    },
  });

  const formProps = useForm<Partial<Account>>({
    defaultValues: queryGetAccount.data,
  });
  const { reset } = formProps;

  useEffect(() => {
    reset(queryGetAccount.data);
  }, [queryGetAccount.data, reset]);

  const isLoading = queryGetAccount.isLoading || !queryGetAccount.data;

  return (
    <FormProvider {...formProps}>
      <SectionContainer>
        <SectionHeader title="General settings" />
        {isLoading ?
          <Flex justifyContent="center" my="40px">
            <Spinner />
          </Flex>
        : <SettingsStack>
            <SettingRowBase
              label="Account name"
              description="The name for this account. It will also appear on the Crypto Tax Report."
            >
              <Box maxW="15em">
                <AccountNameEditable account={queryGetAccount.data} />
              </Box>
            </SettingRowBase>
            <ReadOnlySettingRow
              label="BGL account ID"
              description="The unique identifier to use when setting up the BGL sync integration."
              value={queryGetAccount.data._id}
              onCopy={onCopyBglId.onCopy}
            />
            <Divider />
            <SettingRowBase
              label="Nickname"
              description="A nickname or reference you can use for this account. Only you can see it; other users with account access will not see it."
            >
              <Box maxW="15em">
                <NicknameEditable account={queryGetAccount.data} />
              </Box>
            </SettingRowBase>
          </SettingsStack>
        }
      </SectionContainer>
    </FormProvider>
  );
}
